<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯分享換好康活動' : '新增分享換好康活動' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            {{ isEdit ? '更新中...' : '建立中...' }}
          </span>
          <span v-else>
            {{ isEdit ? '更新活動' : '建立活動' }}
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="status-banner" :class="form.isActive ? 'is-on' : 'is-off'">
          <div class="status-banner-label">活動狀態</div>
          <div class="status-banner-main">
            <div class="status-banner-value">{{ form.isActive ? '已啟用' : '已停用' }}</div>
            <label class="toggle-switch">
              <input v-model="form.isActive" type="checkbox" />
              <span class="toggle-slider"></span>
            </label>
          </div>
          <p class="status-banner-hint">同一時間只能啟用一檔。停用時，會員從 LIFF 進入連結不會看到此活動。</p>
        </div>

        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>

          <div class="form-row">
            <div class="form-group">
              <label>活動代碼</label>
              <input
                v-model.trim="form.campaignCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.campaignCode }"
                placeholder="留空將自動生成"
                :disabled="isEdit"
              />
              <p class="form-hint">僅限英文、數字與底線（_），留空將自動生成</p>
              <div v-if="errors.campaignCode" class="invalid-feedback">
                {{ errors.campaignCode }}
              </div>
            </div>
            <div class="form-group">
              <label class="required">活動名稱</label>
              <input
                v-model="form.campaignName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.campaignName }"
                placeholder="例如：分享 3 位好友拿點數"
              />
              <div v-if="errors.campaignName" class="invalid-feedback">
                {{ errors.campaignName }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>活動辦法</label>
            <textarea
              v-model="form.ruleDescription"
              class="form-control"
              rows="4"
              placeholder="請輸入活動辦法"
            ></textarea>
          </div>

          <div class="form-group">
            <label>活動說明</label>
            <textarea
              v-model="form.description"
              class="form-control"
              rows="4"
              placeholder="請輸入活動說明"
            ></textarea>
          </div>

          <div class="form-group">
            <label>活動圖片</label>
            <ImageUploader
              v-model="form.imageUrl"
              :uploadFn="uploadImage"
              shape="card"
              hint="建議尺寸 1280 x 720"
            />
          </div>

          <div class="form-group">
            <label class="required">LINE OA</label>
            <select v-model="form.lineConfigId" class="form-control" :class="{ 'is-invalid': errors.lineConfigId }">
              <option value="">請選擇 LINE OA</option>
              <option v-for="config in lineConfigs" :key="config.id" :value="config.id">
                {{ config.configName || config.channelName || config.channelId }}
              </option>
            </select>
            <div v-if="errors.lineConfigId" class="invalid-feedback">
              {{ errors.lineConfigId }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="required">開始時間</label>
              <input
                v-model="form.startAt"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.startAt }"
              />
              <div v-if="errors.startAt" class="invalid-feedback">
                {{ errors.startAt }}
              </div>
            </div>
            <div class="form-group">
              <label>結束時間</label>
              <input
                v-model="form.endAt"
                type="datetime-local"
                class="form-control"
                :class="{ 'is-invalid': errors.endAt }"
              />
              <div v-if="errors.endAt" class="invalid-feedback">
                {{ errors.endAt }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">好友數門檻規則</h3>
            <button type="button" class="btn btn-primary" @click="addRule">
              <i class="ri-add-line"></i>
              新增門檻
            </button>
          </div>

          <div class="reward-list">
            <div v-for="(rule, index) in form.rules" :key="rule.uid" class="reward-card">
              <div class="reward-card-header">
                <strong>門檻 {{ index + 1 }}</strong>
                <button type="button" class="btn btn-sm btn-danger" @click="removeRule(index)">
                  刪除
                </button>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="required">好友達標數</label>
                  <input v-model.number="rule.targetFriendCount" type="number" min="1" class="form-control" />
                </div>
                <div class="form-group">
                  <label>是否可循環</label>
                  <select v-model="rule.repeatable" class="form-control">
                    <option :value="false">否</option>
                    <option :value="true">是</option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>最大循環次數</label>
                  <input
                    v-model.number="rule.maxCycleCount"
                    type="number"
                    min="0"
                    class="form-control"
                    :disabled="!rule.repeatable"
                    placeholder="0 表示不限"
                  />
                </div>
                <div class="form-group">
                  <label>門檻說明</label>
                  <input v-model="rule.description" type="text" class="form-control" placeholder="例如：每 3 位好友送 10 點" />
                </div>
              </div>

              <div class="section-subtitle">達標動作</div>
              <div class="reward-list nested-list">
                <div v-for="(action, actionIndex) in rule.actions" :key="action.uid" class="reward-card nested-card">
                  <div class="reward-card-header">
                    <strong>動作 {{ actionIndex + 1 }}</strong>
                    <button type="button" class="btn btn-sm btn-danger" @click="removeAction(index, actionIndex)">
                      刪除
                    </button>
                  </div>

                  <div class="form-row">
                    <div class="form-group">
                      <label>動作類型</label>
                      <select v-model="action.type" class="form-control">
                        <option value="points">點數</option>
                        <option value="coupon">優惠券</option>
                        <option value="lottery">抽獎</option>
                        <option value="message">訊息通知</option>
                        <option value="tag">貼標籤</option>
                        <option v-if="isWixtarClient || action.type === 'wixtar_ticket'" value="wixtar_ticket">Wixtar 票券</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label>動作說明</label>
                      <input v-model="action.description" type="text" class="form-control" placeholder="內部備註" />
                    </div>
                  </div>

                  <div v-if="action.type === 'points'" class="form-row">
                    <div class="form-group">
                      <label>點數</label>
                      <input v-model.number="action.points" type="number" min="1" class="form-control" />
                    </div>
                  </div>

                  <div v-else-if="action.type === 'coupon'" class="form-row">
                    <div class="form-group full-width">
                      <label>優惠券範本</label>
                      <select
                        v-model="action.couponTemplateId"
                        class="form-control"
                        :disabled="couponTemplatesLoading"
                      >
                        <option value="">請選擇優惠券範本</option>
                        <option v-for="t in couponTemplates" :key="t.id" :value="t.id">
                          {{ t.templateName }} — {{ formatCouponTypeText(t.couponType) }} {{ formatDiscountValue(t) }}
                        </option>
                      </select>
                      <RewardStockWarning :template="couponTemplates.find((item) => item.id === action.couponTemplateId)" />
                      <p v-if="couponTemplatesLoading" class="form-hint">載入中...</p>
                      <p v-else-if="!couponTemplates.length" class="form-hint">尚無啟用中的範本，請至優惠券範本管理建立</p>
                    </div>
                  </div>

                  <div v-else-if="action.type === 'wixtar_ticket'" class="form-row">
                    <div class="form-group full-width">
                      <label>Wixtar 票券</label>
                      <WixtarTicketMultiSelect
                        :model-value="action.wixtarTicketIds"
                        :tickets="wixtarTickets"
                        :loading="wixtarTicketsLoading"
                        @update:model-value="action.wixtarTicketIds = $event"
                      />
                      <p class="form-hint">可選擇多張票券，會員需已綁定 Wixtar。</p>
                    </div>
                  </div>

                  <div v-else-if="action.type === 'lottery'" class="form-row">
                    <div class="form-group full-width">
                      <label>抽獎工具</label>
                      <select
                        v-model="action.lotteryConfigId"
                        class="form-control"
                        :disabled="lotteryConfigsLoading"
                      >
                        <option value="">請選擇抽獎工具</option>
                        <option v-for="c in lotteryConfigs" :key="c.id" :value="c.id">
                          {{ lotteryConfigOptionLabel(c) }}
                        </option>
                      </select>
                      <p v-if="lotteryConfigsLoading" class="form-hint">載入中...</p>
                      <p v-else-if="!lotteryConfigs.length" class="form-hint">尚無啟用中的抽獎工具，請至抽獎管理建立</p>
                    </div>
                  </div>

                  <div v-else-if="action.type === 'message'" class="form-row">
                    <div class="form-group full-width line-msg-picker-block">
                      <label>LINE 訊息範本</label>
                      <LineMsgTemplatePicker
                        :key="`${rule.uid}-${action.uid}-line-msg`"
                        audience-filter="personal"
                        :model-value="action.lineMsgTemplate"
                        :selected-id="action.templateCode || null"
                        @update:model-value="(t) => onMessageTemplateChange(action, t)"
                      />
                      <div class="picker-goto-link">
                        <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
                          <i class="ri-add-line"></i> 新增 LINE 訊息範本
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="action.type === 'tag'" class="form-row">
                    <div class="form-group full-width">
                      <label>要貼上的標籤</label>
                      <TagSelectorMultiple v-model="action.tagIds" label="" />
                    </div>
                  </div>
                </div>
              </div>

              <button type="button" class="btn btn-secondary btn-add-action" @click="addAction(index)">
                <i class="ri-add-line"></i>
                新增動作
              </button>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { shareRewardCampaignService } from '@/api/modules/shareRewardCampaigns.js';
import { lineService } from '@/api/modules/line.js';
import { couponService } from '@/api/modules/coupons.js';
import { lotteryService } from '@/api/modules/lottery.js';
import { showError, showSuccess } from '@/utils/swal.js';
import { clientService } from '@/api/modules/clients.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';
import ImageUploader from '@/components/ImageUploader.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import RewardStockWarning from '@/components/RewardStockWarning.vue';

let ruleUid = 0;
let actionUid = 0;

const createDefaultAction = () => ({
  uid: `action-${Date.now()}-${actionUid++}`,
  type: 'points',
  description: '',
  points: 10,
  couponTemplateId: '',
  lotteryConfigId: '',
  templateCode: '',
  lineMsgTemplate: null,
  tagIds: [],
  wixtarTicketIds: []
});

const createDefaultRule = () => ({
  uid: `rule-${Date.now()}-${ruleUid++}`,
  targetFriendCount: 3,
  repeatable: false,
  maxCycleCount: 1,
  description: '',
  actions: [createDefaultAction()]
});

export default {
  name: 'ShareRewardCampaignCreate',
  components: {
    ImageUploader,
    LineMsgTemplatePicker,
    TagSelectorMultiple,
    WixtarTicketMultiSelect,
    RewardStockWarning
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params?.id);
    const campaignId = computed(() => route.params?.id || '');
    const submitting = ref(false);
    const lineConfigs = ref([]);
    const couponTemplates = ref([]);
    const couponTemplatesLoading = ref(false);
    const lotteryConfigs = ref([]);
    const lotteryConfigsLoading = ref(false);
    const isWixtarClient = ref(false);
    const memberSystemLoaded = ref(false);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);

    const form = reactive({
      campaignCode: '',
      campaignName: '',
      description: '',
      ruleDescription: '',
      imageUrl: '',
      lineConfigId: '',
      startAt: '',
      endAt: '',
      isActive: false,
      rules: [createDefaultRule()]
    });

    const errors = reactive({
      campaignCode: '',
      campaignName: '',
      lineConfigId: '',
      startAt: '',
      endAt: ''
    });

    const CAMPAIGN_CODE_PATTERN = /^[A-Za-z0-9_]+$/;

    const resetErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = '';
      });
    };

    const validateForm = () => {
      resetErrors();
      let valid = true;

      const campaignCode = form.campaignCode?.trim() || '';
      if (campaignCode && !CAMPAIGN_CODE_PATTERN.test(campaignCode)) {
        errors.campaignCode = '活動代碼僅能使用英文、數字與底線（_）';
        valid = false;
      }

      if (!form.campaignName || !form.campaignName.trim()) {
        errors.campaignName = '活動名稱為必填欄位';
        valid = false;
      }

      if (!form.lineConfigId) {
        errors.lineConfigId = 'LINE OA 為必填欄位';
        valid = false;
      }

      if (!form.startAt) {
        errors.startAt = '開始時間為必填欄位';
        valid = false;
      }

      if (form.startAt && form.endAt) {
        const start = new Date(form.startAt);
        const end = new Date(form.endAt);
        if (end <= start) {
          errors.endAt = '結束時間必須晚於開始時間';
          valid = false;
        }
      }

      if (!form.rules.length) {
        showError('驗證失敗', '至少需要設定一個好友門檻');
        return false;
      }

      for (const rule of form.rules) {
        if (!rule.targetFriendCount || rule.targetFriendCount <= 0) {
          showError('驗證失敗', '好友達標數必須大於 0');
          return false;
        }
        if (!rule.actions.length) {
          showError('驗證失敗', '每個門檻至少需要一個動作');
          return false;
        }
        for (const action of rule.actions) {
          if (action.type === 'coupon' && !String(action.couponTemplateId || '').trim()) {
            showError('驗證失敗', '請為「優惠券」動作選擇範本');
            return false;
          }
          if (action.type === 'lottery' && !String(action.lotteryConfigId || '').trim()) {
            showError('驗證失敗', '請為「抽獎」動作選擇抽獎工具');
            return false;
          }
          if (action.type === 'message' && !String(action.templateCode || '').trim()) {
            showError('驗證失敗', '請為「訊息通知」動作選擇 LINE 訊息範本');
            return false;
          }
          if (action.type === 'tag' && !(action.tagIds || []).length) {
            showError('驗證失敗', '請為「貼標籤」動作至少選擇一個標籤');
            return false;
          }
          if (action.type === 'wixtar_ticket' && !(action.wixtarTicketIds || []).length) {
            showError('驗證失敗', '請為「Wixtar 票券」動作至少選擇一張票券');
            return false;
          }
        }
      }

      return valid;
    };

    const toDateTimeLocal = (value) => {
      if (!value) return '';
      const date = new Date(value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const buildPayload = () => {
      const payload = {
      campaignName: form.campaignName.trim(),
      description: form.description ? form.description.trim() : null,
      ruleDescription: form.ruleDescription ? form.ruleDescription.trim() : null,
      imageUrl: form.imageUrl || null,
      lineConfigId: form.lineConfigId,
      startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
      endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
      isActive: form.isActive,
      rules: form.rules.map((rule) => ({
        targetFriendCount: rule.targetFriendCount,
        repeatable: !!rule.repeatable,
        maxCycleCount: rule.repeatable ? Number(rule.maxCycleCount || 0) : 1,
        description: rule.description || null,
        actions: rule.actions
          .filter((action) => !(memberSystemLoaded.value && !isWixtarClient.value && action.type === 'wixtar_ticket'))
          .map((action) => {
          const actionConfig = {
            description: action.description || null
          };
          if (action.type === 'points') {
            actionConfig.points = Number(action.points || 0);
          } else if (action.type === 'coupon') {
            actionConfig.couponTemplateId = action.couponTemplateId || null;
          } else if (action.type === 'lottery') {
            actionConfig.lotteryConfigId = action.lotteryConfigId || null;
          } else if (action.type === 'message') {
            const code =
              action.templateCode ||
              (action.lineMsgTemplate &&
                (action.lineMsgTemplate.templateCode || action.lineMsgTemplate.id)) ||
              null;
            actionConfig.templateCode = code || null;
          } else if (action.type === 'tag') {
            actionConfig.tagIds = action.tagIds || [];
          } else if (action.type === 'wixtar_ticket') {
            actionConfig.wixtarTicketIds = action.wixtarTicketIds || [];
          }
          return {
            actionType: action.type,
            actionConfig
          };
        })
      }))
    };

      const campaignCode = form.campaignCode?.trim();
      if (campaignCode) {
        payload.campaignCode = campaignCode;
      }

      return payload;
    };

    const formatCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type || '';
    };

    const formatDiscountValue = (template) => {
      if (!template) return '';
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      }
      if (template.couponType === 'fixed_amount') {
        return `$${Number(template.discountValue).toLocaleString('zh-TW')}`;
      }
      if (template.couponType === 'free_shipping') {
        return '免運費';
      }
      if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const lotteryToolTypeLabel = (toolType) => {
      const map = { wheel: '轉盤', scratch: '刮刮卡', slot: '拉霸', grid: '九宮格' };
      return map[toolType] || toolType;
    };

    const lotteryConfigOptionLabel = (c) => {
      const base = c.configName || c.configKey || c.id;
      return c.toolType ? `${base}（${lotteryToolTypeLabel(c.toolType)}）` : base;
    };

    const loadCouponTemplates = async () => {
      couponTemplatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({ pageSize: 200, isActive: true });
        if (response?.success && response.data) {
          couponTemplates.value = response.data.items || [];
        }
      } catch (err) {
        console.error('載入優惠券範本失敗:', err);
      } finally {
        couponTemplatesLoading.value = false;
      }
    };

    const loadLotteryConfigs = async () => {
      lotteryConfigsLoading.value = true;
      try {
        const response = await lotteryService.getConfigs({ pageSize: 100, isActive: true });
        if (response?.success && response.data) {
          lotteryConfigs.value = response.data.items || [];
        }
      } catch (err) {
        console.error('載入抽獎工具失敗:', err);
      } finally {
        lotteryConfigsLoading.value = false;
      }
    };

    const loadLineConfigs = async () => {
      try {
        const response = await lineService.getConfigs({ page: 1, pageSize: 100 });
        if (response?.success && response.data) {
          lineConfigs.value = response.data.items || response.data || [];
          if (lineConfigs.value.length === 1 && !form.lineConfigId) {
            form.lineConfigId = lineConfigs.value[0].id;
          }
        }
      } catch (err) {
        console.error('載入 LINE OA 設定失敗:', err);
      }
    };

    const loadCampaign = async () => {
      if (!isEdit.value) return;
      try {
        const response = await shareRewardCampaignService.getCampaign(campaignId.value);
        if (response?.success && response.data) {
          const campaign = response.data;
          form.campaignCode = campaign.campaignCode || '';
          form.campaignName = campaign.campaignName || '';
          form.description = campaign.description || '';
          form.ruleDescription = campaign.ruleDescription || '';
          form.imageUrl = campaign.imageUrl || '';
          form.lineConfigId = campaign.lineConfigId || '';
          form.startAt = toDateTimeLocal(campaign.startAt);
          form.endAt = toDateTimeLocal(campaign.endAt);
          form.isActive = !!campaign.isActive;
          form.rules = (campaign.rules || []).map((rule) => ({
            uid: `rule-${rule.id || ruleUid++}`,
            targetFriendCount: rule.targetFriendCount,
            repeatable: !!rule.repeatable,
            maxCycleCount: rule.maxCycleCount ?? 1,
            description: rule.description || '',
            actions: (rule.actions || []).map((action) => {
              const cfg = action.actionConfig || action.config || {};
              const type = action.actionType || action.type || 'points';
              return {
                uid: `action-${action.id || actionUid++}`,
                type,
                description: cfg.description || '',
                points: cfg.points ?? 10,
                couponTemplateId: cfg.couponTemplateId || '',
                lotteryConfigId: cfg.lotteryConfigId || cfg.lotteryEventId || '',
                templateCode: cfg.templateCode || '',
                lineMsgTemplate: null,
                tagIds: cfg.tagIds || [],
                wixtarTicketIds: cfg.wixtarTicketIds || []
              };
            })
          }));

          if (!form.rules.length) {
            form.rules = [createDefaultRule()];
          }
        }
      } catch (err) {
        console.error('載入分享換好康活動失敗:', err);
        await showError('載入失敗', err.message || '無法載入活動');
        router.push({ name: 'ShareRewardCampaignList' });
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) return;

      submitting.value = true;
      try {
        const payload = buildPayload();
        const response = isEdit.value
          ? await shareRewardCampaignService.updateCampaign(campaignId.value, payload)
          : await shareRewardCampaignService.createCampaign(payload);

        if (response?.success) {
          await showSuccess(isEdit.value ? '更新成功' : '建立成功', `活動已${isEdit.value ? '更新' : '建立'}`);
          if (isEdit.value) {
            await loadCampaign();
          } else {
            router.push({ name: 'EditShareRewardCampaign', params: { id: response.data.id } });
          }
        } else {
          await showError(isEdit.value ? '更新失敗' : '建立失敗', response?.message || '操作失敗');
        }
      } catch (err) {
        console.error('儲存分享換好康活動失敗:', err);
        await showError('儲存失敗', err.message || '儲存活動失敗');
      } finally {
        submitting.value = false;
      }
    };

    const addRule = () => {
      form.rules.push(createDefaultRule());
    };

    const removeRule = (index) => {
      if (form.rules.length === 1) {
        showError('無法刪除', '至少需要保留一個門檻');
        return;
      }
      form.rules.splice(index, 1);
    };

    const addAction = (ruleIndex) => {
      form.rules[ruleIndex].actions.push(createDefaultAction());
    };

    const removeAction = (ruleIndex, actionIndex) => {
      const actions = form.rules[ruleIndex].actions;
      if (actions.length === 1) {
        showError('無法刪除', '每個門檻至少需要保留一個動作');
        return;
      }
      actions.splice(actionIndex, 1);
    };

    const handleCancel = () => {
      router.push({ name: 'ShareRewardCampaignList' });
    };

    const uploadImage = async (file) => {
      return shareRewardCampaignService.uploadImage(file);
    };

    const onMessageTemplateChange = (action, template) => {
      action.lineMsgTemplate = template;
      action.templateCode = template ? template.templateCode || template.id : '';
    };

    const loadWixtarContext = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) {
        memberSystemLoaded.value = true;
        return;
      }
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.success && res.data?.clientData?.memberSystemType === 'WIXTAR';
        memberSystemLoaded.value = true;
      } catch {
        memberSystemLoaded.value = false;
        return;
      }
      if (!isWixtarClient.value) return;
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (err) {
        console.error('載入 Wixtar 票券失敗:', err);
        wixtarTickets.value = [];
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([loadLineConfigs(), loadCouponTemplates(), loadLotteryConfigs(), loadWixtarContext()]);
      await loadCampaign();
    });

    return {
      isEdit,
      form,
      errors,
      lineConfigs,
      couponTemplates,
      couponTemplatesLoading,
      lotteryConfigs,
      lotteryConfigsLoading,
      isWixtarClient,
      wixtarTickets,
      wixtarTicketsLoading,
      formatCouponTypeText,
      formatDiscountValue,
      lotteryToolTypeLabel,
      lotteryConfigOptionLabel,
      onMessageTemplateChange,
      submitting,
      handleSubmit,
      handleCancel,
      addRule,
      removeRule,
      addAction,
      removeAction,
      uploadImage
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_form-common.scss';

.checkbox-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reward-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.nested-list {
  margin-top: 12px;
}

.reward-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #fafafa;
}

.nested-card {
  background: #fff;
}

.reward-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.full-width {
  flex: 1 1 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-subtitle {
  margin-top: 12px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #475569;
}

.btn-add-action {
  margin-top: 12px;
}

.status-banner {
  margin-bottom: 16px;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid transparent;
}

.status-banner-main {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
}

.status-banner.is-off {
  background: #fff7ed;
  border-color: #fdba74;
}

.status-banner.is-on {
  background: #f0fdf4;
  border-color: #86efac;
}

.status-banner-label {
  font-size: 13px;
  color: #666;
}

.status-banner-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.status-banner.is-off .status-banner-value {
  color: #c2410c;
}

.status-banner.is-on .status-banner-value {
  color: #15803d;
}

.status-banner-hint {
  margin: 6px 0 0;
  font-size: 13px;
  color: #666;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #d1d5db;
  border-radius: 28px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 22px;
  height: 22px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(24px);
}
</style>
